import { getLocale } from './index';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en'; // locale-data for en
import '@formatjs/intl-numberformat/locale-data/fr'; // locale-data for en
import { convertToCurrentCurrency, currentCurrency } from './currency';

// prettier-ignore
const currencyDecimals: Record<string, number> = {
  BHD: 3, BIF: 0, BYR: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0, JOD: 3, JPY: 0, KMF: 0, KRW: 0, KWD: 3, LTL: 0, LVL: 0, LYD: 3,
  MRO: 0, OMR: 3, PYG: 0, RWF: 0, STD: 0, TND: 3, UGX: 0, VND: 0, VUV: 0, XAF: 0, XAG: 0, XAU: 0, XDR: 0, XOF: 0, XPF: 0, ZMK: 0, UYI: 0,
  UYW: 4,
};

export const createTwoDigitsRound =
  (roundFunction: (val: number) => number) => (val: number) => {
    let numberOfDigits = 2;

    while (Math.pow(10, numberOfDigits) < val) {
      numberOfDigits++;
    }

    const roundTarget = Math.pow(10, numberOfDigits - 2);
    const res = roundFunction(val / roundTarget) * roundTarget;
    return res;
  };

export const toCurrency = (
  value: number,
  hideDigits: boolean = false,
  currency?: string,
  _locale?: string,
  options: Intl.NumberFormatOptions & { compact?: boolean } = {},
) => {
  const locale = _locale || getLocale() || 'fr_FR';

  let decimals = currencyDecimals[currency || 'EUR'];
  if (decimals === undefined) {
    decimals = 2;
  }
  const factor = Math.pow(10, decimals);
  const digits = hideDigits && value % factor === 0 ? 0 : 2;

  const res = Intl.NumberFormat(locale || 'fr_FR', {
    style: currency ? 'currency' : 'decimal',
    currency: currency,
    minimumFractionDigits: digits,
    unitDisplay: 'narrow',
    notation: options.compact ? 'compact' : 'standard',
    ...options,
  }).format(value / factor);

  if (options.compact) {
    // hack for invalid french notation
    const regRes = /^(.+)(([kM(Md)(Bn)]) ([€$£]))$/.exec(res);
    if (regRes) {
      // remove the space between k and €
      return regRes[1] + regRes[3] + regRes[4];
    }
  }
  return res;
};

export const toEuros = (
  value: number,
  hideDigits: boolean = false,
  locale?: string,
  options?: Intl.NumberFormatOptions & { compact?: boolean },
) =>
  toCurrency(
    convertToCurrentCurrency(value),
    hideDigits,
    currentCurrency,
    locale,
    options,
  );

export const toPercent = (value: number, _locale?: string) => {
  const locale = _locale || getLocale() || 'fr_FR';
  return Intl.NumberFormat(locale || 'fr_FR', {
    style: 'percent',
    minimumFractionDigits: 1,
  }).format(value);
};

export const twoDigitsRound = createTwoDigitsRound(Math.round);
export const twoDigitsCeil = createTwoDigitsRound(Math.ceil);
export const twoDigitsFloor = createTwoDigitsRound(Math.floor);

// format from 88189618700039 to 881 896 187 00039
export const formatSiret = (siret?: string) => {
  if (!siret) {
    return siret;
  }

  const digits = siret.replace(/\D/g, '');

  // A SIRET is a 9-digit SIREN (grouped by 3) followed by a 5-digit NIC.
  if (digits.length !== 14) {
    return siret;
  }

  const siren = digits.slice(0, 9).replace(/(\d{3})(?=\d)/g, '$1 ');
  const nic = digits.slice(9);

  return `${siren} ${nic}`;
};

// format from FR90881896187 to FR90 881 896 187
export const formatVat = (vat?: string) => {
  if (!vat) {
    return vat;
  }

  // French VAT: 2-letter country code + 2-char key + 9-digit SIREN.
  const match = /^([A-Z]{2})(\w{2})(\d{9})$/i.exec(vat.replace(/\s/g, ''));
  if (!match) {
    return vat;
  }

  const [, country, key, siren] = match;
  const groupedSiren = siren.replace(/(\d{3})(?=\d)/g, '$1 ');

  return `${country.toUpperCase()}${key} ${groupedSiren}`;
};
